import { useRef, useState } from "react";

import { FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import { theme } from "@/core/theme";
import { SEND_PATIENT_REGISTRATION_VERIFICATION_EMAIL_MUTATION } from "@/graphql/organisations/mutations";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useDocTitle from "@/hooks/use-doc-title";
import Checkbox from "@/tpo/Checkbox";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import { Error } from "@/tpo/InputWrapper";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import { IndicativeButton } from "@/v2/Buttons";

function VerifyEmailSubmittedConfirmation({ email }) {
  return (
    <CustomerPage
      bg="haze"
      includeFooter
      jumbotronProps={{ bg: "partners", title: "Patient Registration", color: "dark" }}
    >
      <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
        <Box maxWidth={760} mx="auto">
          <Stack bg="white" pt={[60]} pb={[80]} px={[40]} borderRadius={5} gap={16}>
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              Verification email sent
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={1.5}>
              An email has been sent to {email} please click on the link in that email to continue.
            </Box>
          </Stack>
        </Box>
      </Box>
    </CustomerPage>
  );
}

export default function OrganisationPatientRegistrationVerifyEmailPage() {
  const { organisationId } = useParams();
  const [submittedEmail, setSubmittedEmail] = useState(null);

  useDocTitle("Patient Registration");

  const submitButtonRef = useRef();

  const { methods, onSubmit, nonFieldError } = useDjangoGraphqlForm({
    mutation: SEND_PATIENT_REGISTRATION_VERIFICATION_EMAIL_MUTATION,
    mutationName: "sendPatientRegistrationVerificationEmailMutation",
    defaultValues: {
      email: "",
      isForChild: false
    },
    transformer: input => ({
      ...input,
      organisation: organisationId
    }),
    handleSuccess: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
      }
      setSubmittedEmail(methods.getValues("email"));
    },
    handleFailure: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  if (submittedEmail) {
    return <VerifyEmailSubmittedConfirmation email={submittedEmail} />;
  }

  return (
    <FormProvider {...methods}>
      <CustomerPage
        bg="haze"
        includeFooter
        jumbotronProps={{
          bg: "partners",
          title: "Patient Registration",
          color: "dark"
        }}
      >
        <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
          <Box maxWidth={760} mx="auto">
            <Stack
              as="form"
              gap={[20, 20, 40]}
              onSubmit={e => {
                methods.clearErrors();
                methods.handleSubmit(onSubmit)(e);
              }}
            >
              <Stack bg="white" pt={[60]} pb={[80]} px={[40]} borderRadius={5}>
                <Stack gap={40}>
                  <Stack gap={16}>
                    <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                      Verify your email address to continue
                    </Box>
                    <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={1.5}>
                      Please enter your email address below and submit and we’ll send you a secure
                      link to continue capturing your details
                    </Box>
                  </Stack>
                  <ControlledFormField
                    name="email"
                    label="Email"
                    Component={FloatingLabelInput}
                    mt={-40 + 28}
                  />
                  <ControlledFormField
                    name="isForChild"
                    label="I'm registering on behalf of a child (I'll be their guardian)"
                    Component={Checkbox}
                    styles={{
                      labelProps: {
                        fontSize: [12, 12, 14],
                        lineHeight: "24px"
                      },
                      checkbox: {
                        size: ["md", "md", "lg"]
                      }
                    }}
                  />
                  {!!nonFieldError && (
                    <>
                      <Spacer py={1} />
                      <Error error={nonFieldError} />
                    </>
                  )}
                  <IndicativeButton
                    ref={submitButtonRef}
                    type="submit"
                    defaultColor="green"
                    pendingColor="#0cf970"
                    successColor="#2ecc71"
                    failureColor="error"
                    size={["sm", "sm", "md"]}
                    sx={{ mx: "auto" }}
                    rightIcon={<ChevronComponent />}
                  >
                    send
                  </IndicativeButton>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </CustomerPage>
    </FormProvider>
  );
}
